<template>
  <draggable-panel
    :visible="visible"
    :title="title"
    :icon="icon"
    :width="1200"
    :tabs="tabs"
    :active-tab="currentActiveTab"
    :initial-position="{ top: 60 }"
    :z-index="zIndex"
    :enable-expand="true"
    :is-expanded="isExpanded"
    :expanded-width="1460"
    @close="handleClose"
    @tab-change="handleTabChange"
    @panel-focus="handlePanelFocus"
    @expand-toggle="handleExpandToggle"
  >
    <!-- 内容区域 -->
    <div class="panel-content">
      <!-- 预警规则（仅显示，数据来自规则内容解析） -->
      <div v-show="currentActiveTab === 'rules'" class="alert-rules-container" v-loading="loadingRules">
        <div class="alert-rules-table">
          <el-table
            :data="displayRules"
            border
            stripe
            style="width: 100%"
            size="small"
            height="400"
          >
            <!-- 分类列 -->
            <el-table-column prop="category" label="分类" width="80" align="center">
              <template slot-scope="scope">
                <span
                  class="category-cell"
                  :style="{ backgroundColor: scope.row.backgroundColor, color: scope.row.textColor, fontWeight: 'bold' }"
                >
                  {{ scope.row.category }}
                </span>
              </template>
            </el-table-column>

            <!-- 级别列 -->
            <el-table-column prop="level" label="级别" width="110" align="center">
              <template slot-scope="scope">
                <span
                  class="level-cell"
                  :style="{ backgroundColor: scope.row.backgroundColor, color: scope.row.textColor, fontWeight: 'bold' }"
                >
                  {{ scope.row.level }}
                </span>
              </template>
            </el-table-column>

            <!-- 规则列 -->
            <el-table-column prop="rule" label="规则" min-width="200" align="center">
              <template slot-scope="scope">
                <div class="rule-content">{{ scope.row.rule }}</div>
              </template>
            </el-table-column>

            <!-- 判别途径列 -->
            <el-table-column prop="detectionMethod" label="判别途径" width="140" align="center">
              <template slot-scope="scope">
                <div class="detection-method">{{ scope.row.detectionMethod }}</div>
              </template>
            </el-table-column>

            <!-- 处置列 -->
            <el-table-column prop="disposal" label="处置" min-width="300" align="center">
              <template slot="header">
                <div class="disposal-header">
                  <div class="disposal-title">处置</div>
                  <div class="disposal-subtitle">
                    <span class="subtitle-item">系统</span>
                    <span class="subtitle-item">人工</span>
                  </div>
                </div>
              </template>
              <template slot-scope="scope">
                <div class="disposal-content">
                  <div class="system-action">
                    <div class="action-title">系统：</div>
                    <div class="action-items">
                      <div v-for="(action, index) in scope.row.systemAction" :key="index" class="action-item">
                        {{ index + 1 }}、{{ action }}
                      </div>
                    </div>
                  </div>
                  <div class="manual-action">
                    <div class="action-title">人工：</div>
                    <div class="action-content">
                      <div v-if="Array.isArray(scope.row.manualAction)" class="action-items">
                        <div v-for="(action, index) in scope.row.manualAction" :key="index" class="action-item">
                          {{ index + 1 }}、{{ action }}
                        </div>
                      </div>
                      <div v-else class="action-content-text">{{ scope.row.manualAction }}</div>
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 说明信息 -->
        <div class="rules-description">
          <el-alert title="预警规则说明" type="info" :closable="false" show-icon>
            <div class="description-content">
              <p>• <strong>警示（蓝色）</strong>：船舶进入报告线时的基础提醒</p>
              <p>• <strong>警告（黄色）</strong>：船舶进入警戒区偏航的初级警告</p>
              <p>• <strong>警告（橙色）</strong>：船舶进入通航区偏航的中级警告</p>
              <p>• <strong>警告（红色）</strong>：严重违规行为的高级警报</p>
            </div>
          </el-alert>
        </div>
      </div>

      <!-- 规则配置（表单编辑，保存走前端 mock 接口） -->
      <div v-show="currentActiveTab === 'config'" class="rules-config-container">
        <!-- 配置工具栏 -->
        <div class="rules-toolbar">
          <div class="rules-toolbar-left">
            <span class="bridge-tag">当前桥梁：{{ bridgeName }}</span>
          </div>
          <div class="rules-toolbar-right">
            <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAddRule">添加预警规则</el-button>
          </div>
        </div>

        <el-table
          :data="displayRules"
          border
          stripe
          style="width: 100%"
          size="small"
          height="380"
          v-loading="loadingRules"
        >
          <el-table-column prop="ruleName" label="规则名称" min-width="150" show-overflow-tooltip></el-table-column>
          <el-table-column prop="category" label="分类" width="80" align="center">
            <template slot-scope="scope">
              <span class="plain-tag">{{ scope.row.category }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="level" label="级别" width="100" align="center">
            <template slot-scope="scope">
              <span
                class="level-tag"
                :style="{ backgroundColor: scope.row.backgroundColor, color: scope.row.textColor }"
              >{{ scope.row.level }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="rule" label="规则" min-width="220" align="center" show-overflow-tooltip></el-table-column>
          <el-table-column prop="detectionMethod" label="判别途径" width="130" align="center"></el-table-column>
          <el-table-column prop="updateTime" label="更新时间" width="160" align="center"></el-table-column>
          <el-table-column label="操作" width="140" align="center" fixed="right">
            <template slot-scope="scope">
              <div class="action-icons">
                <el-tooltip content="编辑" placement="top">
                  <el-button size="mini" type="primary" icon="el-icon-edit" circle @click="handleEdit(scope.row)"></el-button>
                </el-tooltip>
                <el-tooltip content="删除" placement="top">
                  <el-button size="mini" type="danger" icon="el-icon-delete" circle @click="handleDelete(scope.row)"></el-button>
                </el-tooltip>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- 配置说明 -->
        <div class="config-message">
          <p>「规则配置」用于维护当前桥梁（{{ bridgeName }}）的预警规则：可新增、编辑、删除。</p>
          <p>编辑内容与「预警规则」展示表字段一致（分类/级别/规则/判别途径/处置），样式由系统按级别自动匹配。</p>
          <p>修改后点击「保 存」即生效，「预警规则」页面将同步展示最新规则内容。</p>
        </div>
      </div>
    </div>

    <!-- 规则编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="680px"
      append-to-body
      :modal-append-to-body="false"
      @closed="resetForm">
      <el-form :model="ruleForm" :rules="rules" ref="ruleForm" label-width="90px" label-position="left">
        <el-form-item label="规则名称" prop="ruleName">
          <el-input v-model="ruleForm.ruleName" placeholder="请输入规则名称，如：进入报告线警示"></el-input>
        </el-form-item>
        <el-form-item label="分类" prop="category">
          <el-select v-model="ruleForm.category" placeholder="请选择分类">
            <el-option label="警示" value="警示"></el-option>
            <el-option label="警告" value="警告"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="级别" prop="level">
          <el-select v-model="ruleForm.level" placeholder="请选择级别">
            <el-option label="蓝色" value="蓝色"></el-option>
            <el-option label="(黄色)" value="(黄色)"></el-option>
            <el-option label="(橙色)" value="(橙色)"></el-option>
            <el-option label="(红色)" value="(红色)"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="规则" prop="rule">
          <el-input
            type="textarea"
            :rows="2"
            placeholder="请输入规则描述，如：判别有船进入报告线"
            v-model="ruleForm.rule">
          </el-input>
        </el-form-item>
        <el-form-item label="判别途径" prop="detectionMethod">
          <el-input v-model="ruleForm.detectionMethod" placeholder="如：AIS/雷达"></el-input>
        </el-form-item>
        <el-form-item label="系统处置">
          <div v-for="(item, index) in ruleForm.systemAction" :key="'sys-' + index" class="action-row">
            <el-input v-model="ruleForm.systemAction[index]" :placeholder="'系统处置 ' + (index + 1)"></el-input>
            <el-button
              v-if="ruleForm.systemAction.length > 1"
              type="danger"
              icon="el-icon-delete"
              size="mini"
              circle
              @click="removeSystemAction(index)">
            </el-button>
          </div>
          <el-button type="text" icon="el-icon-plus" @click="addSystemAction">添加系统处置项</el-button>
        </el-form-item>
        <el-form-item label="人工处置">
          <div v-for="(item, index) in ruleForm.manualAction" :key="'man-' + index" class="action-row">
            <el-input v-model="ruleForm.manualAction[index]" :placeholder="'人工处置 ' + (index + 1)"></el-input>
            <el-button
              v-if="ruleForm.manualAction.length > 1"
              type="danger"
              icon="el-icon-delete"
              size="mini"
              circle
              @click="removeManualAction(index)">
            </el-button>
          </div>
          <el-button type="text" icon="el-icon-plus" @click="addManualAction">添加人工处置项</el-button>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="submitForm">保 存</el-button>
      </div>
    </el-dialog>
  </draggable-panel>
</template>

<script>
import alertRuleApi from '@/api/bridge-alert-rule'
import DraggablePanel from '@/components/common/DraggablePanel.vue'

export default {
  name: 'BridgeAlertRules',
  components: {
    DraggablePanel
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    bridgeId: {
      type: [Number, String],
      default: null
    },
    activeTab: {
      type: String,
      default: 'rules'
    },
    zIndex: {
      type: Number,
      default: 1000
    }
  },
  data() {
    return {
      title: '桥梁预警规则',
      icon: 'el-icon-set-up',
      tabs: [
        { key: 'rules', label: '预警规则' },
        { key: 'config', label: '规则配置' }
      ],
      isExpanded: false,
      internalActiveTab: 'rules',
      // 规则数据（来自 mock 接口，content 为 JSON 字符串）
      bridgeName: '',
      alertRules: [],
      loadingRules: false,
      // 规则编辑对话框
      dialogVisible: false,
      dialogTitle: '添加预警规则',
      editingRuleId: null,
      submitLoading: false,
      ruleForm: {
        ruleName: '',
        category: '',
        level: '',
        rule: '',
        detectionMethod: '',
        systemAction: [''],
        manualAction: ['']
      },
      rules: {
        ruleName: [
          { required: true, message: '请输入规则名称', trigger: 'blur' },
          { min: 2, max: 100, message: '长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        category: [
          { required: true, message: '请选择分类', trigger: 'change' }
        ],
        level: [
          { required: true, message: '请选择级别', trigger: 'change' }
        ],
        rule: [
          { required: true, message: '请输入规则描述', trigger: 'blur' }
        ],
        detectionMethod: [
          { required: true, message: '请输入判别途径', trigger: 'blur' }
        ]
      }
    };
  },
  computed: {
    currentActiveTab() {
      return this.internalActiveTab;
    },
    // 解析 content JSON 供两个 tab 使用（显示 + 配置表格）
    displayRules() {
      return this.alertRules.map(rule => {
        const c = this.parseRuleContent(rule)
        const colors = this.getLevelColors(c.level)
        return {
          ...rule,
          category: c.category || '-',
          level: c.level || '-',
          rule: c.rule || '（内容未包含 rule 字段）',
          detectionMethod: c.detectionMethod || '-',
          systemAction: Array.isArray(c.systemAction)
            ? c.systemAction.map(String)
            : (c.systemAction ? [String(c.systemAction)] : ['-']),
          manualAction: Array.isArray(c.manualAction)
            ? c.manualAction.map(String)
            : (c.manualAction ? String(c.manualAction) : '-'),
          backgroundColor: colors.backgroundColor,
          textColor: colors.textColor
        }
      })
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.init();
      }
    },
    bridgeId: {
      handler() {
        this.fetchBridgeName();
        this.fetchAlertRules();
      },
      immediate: true
    },
    activeTab: {
      handler(newVal) {
        if (newVal && newVal !== this.internalActiveTab) {
          this.internalActiveTab = newVal;
          this.handleTabClick(newVal);
        }
      },
      immediate: true
    },
    internalActiveTab(newVal) {
      this.handleTabClick(newVal);
      this.$emit('update:active-tab', newVal);
    }
  },
  mounted() {
    if (this.$el && this.$el.parentNode !== document.body) {
      document.body.appendChild(this.$el);
    }
  },
  beforeDestroy() {
    if (this.$el && this.$el.parentNode === document.body) {
      document.body.removeChild(this.$el);
    }
  },
  methods: {
    /**
     * 初始化
     */
    init() {
      this.fetchBridgeName();
      this.fetchAlertRules();
    },

    // 当前桥梁名称
    async fetchBridgeName() {
      try {
        const { getAllBridges } = await import('@/api/bridge')
        const res = await getAllBridges()
        if (res.code === 200) {
          const bridge = (res.data || []).find(b => String(b.id) === String(this.bridgeId))
          this.bridgeName = bridge ? bridge.bridgeName : `桥梁 #${this.bridgeId}`
        }
      } catch (e) {
        // ignore
      }
    },

    // 获取告警规则列表（按当前桥梁，mock 接口，一次性加载全部）
    async fetchAlertRules() {
      if (this.bridgeId === null || this.bridgeId === undefined || this.bridgeId === '') return
      try {
        this.loadingRules = true
        const res = await alertRuleApi.getAlertRules({
          bridgeId: this.bridgeId,
          pageNum: 1,
          pageSize: 1000
        })
        if (res.code === 200) {
          this.alertRules = res.data.list || []
        } else {
          this.$message.error(res.message || res.msg || '获取告警规则列表失败')
        }
      } catch (error) {
        console.error('获取告警规则列表出错：', error)
        this.$message.error('获取告警规则列表失败')
      } finally {
        this.loadingRules = false
      }
    },

    // 解析规则内容 JSON（失败返回空对象）
    parseRuleContent(rule) {
      if (!rule || !rule.content) return {}
      try {
        const parsed = typeof rule.content === 'string' ? JSON.parse(rule.content) : rule.content
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
      } catch (e) {
        return {}
      }
    },

    // 按级别自动匹配展示颜色（不允许用户设置样式）
    getLevelColors(level) {
      const lv = String(level || '')
      if (lv.includes('蓝')) return { backgroundColor: '#0f31fd', textColor: '#dfdede' }
      if (lv.includes('黄')) return { backgroundColor: '#e7df00', textColor: '#333333' }
      if (lv.includes('橙')) return { backgroundColor: '#ffb100', textColor: '#333333' }
      if (lv.includes('红')) return { backgroundColor: '#f30505', textColor: '#cdcaca' }
      return { backgroundColor: '#f5f7fa', textColor: '#333333' }
    },

    // 添加规则
    handleAddRule() {
      this.dialogTitle = '添加预警规则'
      this.editingRuleId = null
      this.resetForm()
      this.dialogVisible = true
    },

    // 编辑规则（回填表单）
    async handleEdit(row) {
      this.dialogTitle = '编辑预警规则'
      this.editingRuleId = row.id
      try {
        this.loadingRules = true
        const res = await alertRuleApi.getAlertRuleById(row.id)
        if (res.code === 200) {
          const c = this.parseRuleContent(res.data)
          this.ruleForm = {
            ruleName: res.data.ruleName || '',
            category: c.category || '',
            level: c.level || '',
            rule: c.rule || '',
            detectionMethod: c.detectionMethod || '',
            systemAction: Array.isArray(c.systemAction) && c.systemAction.length
              ? c.systemAction.map(String)
              : [''],
            manualAction: Array.isArray(c.manualAction) && c.manualAction.length
              ? c.manualAction.map(String)
              : (c.manualAction ? [String(c.manualAction)] : [''])
          }
          this.dialogVisible = true
        } else {
          this.$message.error(res.message || res.msg || '获取规则详情失败')
        }
      } catch (error) {
        console.error('获取规则详情出错：', error)
        this.$message.error('获取规则详情失败')
      } finally {
        this.loadingRules = false
      }
    },

    // 删除规则
    handleDelete(row) {
      this.$confirm(`确定删除规则「${row.ruleName}」吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          this.loadingRules = true
          const res = await alertRuleApi.deleteAlertRule(row.id)
          if (res.code === 200) {
            this.$message.success('删除成功')
            this.fetchAlertRules()
          } else {
            this.$message.error(res.message || res.msg || '删除失败')
          }
        } catch (error) {
          console.error('删除告警规则出错：', error)
          this.$message.error('删除失败')
        } finally {
          this.loadingRules = false
        }
      }).catch(() => {
        // 取消删除
      })
    },

    // 提交表单（表单字段组装成 JSON 字符串保存到 mock 接口）
    submitForm() {
      this.$refs.ruleForm.validate(async valid => {
        if (!valid) return
        try {
          this.submitLoading = true
          let res
          const manualActions = this.ruleForm.manualAction
            .map(s => (s || '').trim())
            .filter(Boolean)
          const content = JSON.stringify({
            category: this.ruleForm.category,
            level: this.ruleForm.level,
            rule: this.ruleForm.rule.trim(),
            detectionMethod: this.ruleForm.detectionMethod.trim(),
            systemAction: this.ruleForm.systemAction
              .map(s => (s || '').trim())
              .filter(Boolean),
            manualAction: manualActions.length === 1 ? manualActions[0] : manualActions
          }, null, 2)
          const data = {
            ruleName: this.ruleForm.ruleName.trim(),
            content,
            bridgeId: this.bridgeId
          }
          if (this.editingRuleId) {
            res = await alertRuleApi.updateAlertRule(this.editingRuleId, data)
          } else {
            res = await alertRuleApi.createAlertRule(data)
          }
          if (res.code === 200) {
            this.$message.success(this.editingRuleId ? '更新成功' : '添加成功')
            this.dialogVisible = false
            this.fetchAlertRules()
          } else {
            this.$message.error(res.message || res.msg || (this.editingRuleId ? '更新失败' : '添加失败'))
          }
        } catch (error) {
          console.error(this.editingRuleId ? '更新告警规则出错：' : '添加告警规则出错：', error)
          this.$message.error(this.editingRuleId ? '更新失败' : '添加失败')
        } finally {
          this.submitLoading = false
        }
      })
    },

    // 重置表单
    resetForm() {
      if (this.$refs.ruleForm) {
        this.$refs.ruleForm.resetFields()
      }
      this.ruleForm = {
        ruleName: '',
        category: '',
        level: '',
        rule: '',
        detectionMethod: '',
        systemAction: [''],
        manualAction: ['']
      }
    },

    // 系统处置动态列表
    addSystemAction() {
      this.ruleForm.systemAction.push('')
    },
    removeSystemAction(index) {
      this.ruleForm.systemAction.splice(index, 1)
    },

    // 人工处置动态列表
    addManualAction() {
      this.ruleForm.manualAction.push('')
    },
    removeManualAction(index) {
      this.ruleForm.manualAction.splice(index, 1)
    },

    // 处理标签页切换
    handleTabClick(tabName) {
      if (tabName === 'config') {
        this.fetchAlertRules()
      }
    },

    // 处理关闭
    handleClose() {
      this.$emit('update:visible', false);
      this.$emit('close');
    },

    handleTabChange(tabKey) {
      this.internalActiveTab = tabKey;
    },

    handlePanelFocus() {
      this.$emit('panel-focus');
    },

    handleExpandToggle(expanded) {
      this.isExpanded = expanded;
    }
  }
};
</script>

<style scoped>
/* 面板内容样式 */
.panel-content {
  padding: 0;
  max-height: 70vh;
  overflow-y: auto;
}

/* 预警规则容器样式（仅显示） */
.alert-rules-container {
  padding: 5px;
}

.alert-rules-table {
  margin-bottom: 20px;
}

/* 分类和级别列样式 */
.category-cell,
.level-cell {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  min-width: 60px;
  text-align: center;
}

/* 规则内容样式 */
.rule-content {
  padding: 8px;
  line-height: 1.4;
  text-align: left;
}

/* 判别途径样式 */
.detection-method {
  padding: 8px;
  line-height: 1.4;
}

/* 处置列样式 */
.disposal-header {
  text-align: center;
}

.disposal-title {
  font-size: 16px;
  font-weight: 800;
  margin-bottom: 5px;
}

.disposal-subtitle {
  display: flex;
  justify-content: space-around;
  font-size: 14px;
  font-weight: 600;
}

.subtitle-item {
  flex: 1;
  text-align: center;
}

.disposal-content {
  padding: 10px;
  text-align: left;
}

.action-title {
  font-weight: bold;
  margin-bottom: 8px;
  color: #409EFF;
}

.action-items {
  margin-bottom: 15px;
}

.action-item {
  margin-bottom: 5px;
  line-height: 1.4;
  padding-left: 10px;
}

.action-content-text {
  line-height: 1.4;
  padding-left: 10px;
}

/* 说明信息样式 */
.rules-description {
  margin-top: 20px;
}

.description-content p {
  margin: 5px 0;
  line-height: 1.6;
}

/* 规则配置容器样式（编辑管理） */
.rules-config-container {
  padding: 5px;
}

.rules-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.rules-toolbar-left {
  display: flex;
  align-items: center;
}

.bridge-tag {
  font-size: 13px;
  color: #409EFF;
  font-weight: 600;
}

.rules-toolbar-right {
  display: flex;
  gap: 8px;
}

/* 配置表格标签 */
.plain-tag {
  display: inline-block;
  padding: 2px 8px;
  background-color: #f0f2f5;
  border-radius: 3px;
  font-size: 12px;
}

.level-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
}

/* 操作列图标按钮：强制一行 */
.action-icons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.action-icons .el-button {
  margin-left: 0;
}

.config-message {
  margin-top: 10px;
  padding: 8px 12px;
  background-color: #f0f9eb;
  border-radius: 4px;
  border-left: 3px solid #67c23a;
  font-size: 12px;
  color: #529b2e;
  line-height: 1.6;
}

.config-message p {
  margin: 3px 0;
}

/* 处置动态列表 */
.action-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  width: 100%;
}

.action-row .el-input {
  flex: 1;
}

/* 表格样式优化 */
::v-deep .el-table th {
  background-color: #c9d1ee !important;
  color: #333 !important;
  font-weight: 800 !important;
  text-align: center !important;
  padding: 10px 8px !important;
}

::v-deep .el-table td {
  padding: 8px !important;
  vertical-align: middle !important;
}
</style>
